import { Component, computed, input, output, signal } from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import { RaIconComponent } from 'src/app/shared/components/ra-icon/ra-icon.component';
import { MIN_IMAGE_WIDTH } from '../../../../pdf-renderer/image-size';
import { parseImageWidthInput, stepImageWidth } from '../cards/image-card/image-size.util';

/**
 * Largura da foto em % da coluna, com − e + (de 5 em 5) e o número digitável. Serve o
 * menu "Tamanho" do rail da imagem e o "Personalizado" dos modais de configuração.
 *
 * Emite cada valor válido na hora (`valueChange`), para quem usa mostrar ao vivo, e
 * `commit` no Enter ou na saída do campo.
 */
@Component({
    selector: 'image-width-field',
    templateUrl: 'image-width-field.component.html',
    styleUrl: 'image-width-field.component.scss',
    imports: [RaIconComponent, TranslatePipe],
})
export class ImageWidthFieldComponent {
    /** Largura mostrada, em %. */
    public value = input.required<number>();
    /** Largura livre em uso: o número sai do cinza. */
    public active = input(false);
    public disabled = input(false);

    public valueChange = output<number>();
    public commit = output<void>();

    public readonly min = MIN_IMAGE_WIDTH;

    /** Texto em digitação (null = mostra o valor). */
    private _draft = signal<string | null>(null);
    public text = computed(() => this._draft() ?? String(this.value()));

    public step(direction: 1 | -1): void {
        this._draft.set(null);
        this.valueChange.emit(stepImageWidth(this.value(), direction));
    }

    /** Digitação: só vale no intervalo (o "5" a caminho de "50" não encolhe a foto). */
    public onInput(event: Event): void {
        const raw = (event.target as HTMLInputElement).value;
        this._draft.set(raw);
        const width = parseImageWidthInput(raw);
        if (width !== null && width >= MIN_IMAGE_WIDTH && width <= 100) this.valueChange.emit(width);
    }

    /** Saída do campo: o que ficou fora do intervalo vai para o limite mais perto. */
    public onBlur(): void {
        const draft = this._draft();
        this._draft.set(null);

        const width = draft === null ? null : parseImageWidthInput(draft);
        if (width !== null && (width < MIN_IMAGE_WIDTH || width > 100))
            this.valueChange.emit(Math.min(100, Math.max(MIN_IMAGE_WIDTH, width)));

        this.commit.emit();
    }

    public onKeydown(event: KeyboardEvent): void {
        if (event.key === 'Enter') {
            (event.target as HTMLInputElement).blur();
        } else if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
            event.preventDefault();
            this._draft.set(null);
            const delta = event.key === 'ArrowUp' ? 1 : -1;
            this.valueChange.emit(Math.min(100, Math.max(MIN_IMAGE_WIDTH, this.value() + delta)));
        }
    }
}
